<template>
  <div class="user-ed">
    <div v-if="mode === 'edit' && user" class="user-ed__head">
      <app-avatar :name="displayName" :size="40" />
      <div class="user-ed__ident">
        <span class="user-ed__name">{{ displayName }}</span>
        <span class="user-ed__meta" title="Benutzername kann nicht geändert werden">
          <span class="user-ed__login">{{ user.username }}</span>
          <app-icon name="lock" :size="12" class="user-ed__lock" />
          <span class="user-ed__id">· Benutzer-ID {{ user.user_id }}</span>
        </span>
      </div>
    </div>

    <template v-if="mode === 'create'">
      <div class="app-form-field">
        <label class="app-label" :for="ids.username">Benutzername</label>
        <app-text-field
          :id="ids.username"
          :value="value.username"
          :error="errors.username"
          autocomplete="off"
          autocapitalize="none"
          spellcheck="false"
          @input="set('username', $event)"
        />
      </div>
      <div class="app-form-field">
        <label class="app-label" :for="ids.userId">Benutzer-ID</label>
        <app-text-field
          :id="ids.userId"
          :value="value.user_id"
          :error="errors.user_id"
          inputmode="numeric"
          autocomplete="off"
          @input="set('user_id', $event)"
        />
      </div>
    </template>

    <div class="app-form-field">
      <label class="app-label" :for="ids.fullname">Name</label>
      <app-text-field
        :id="ids.fullname"
        :value="value.fullname"
        :error="errors.fullname"
        autocomplete="off"
        @input="set('fullname', $event)"
      />
    </div>
    <div class="app-form-field">
      <label class="app-label" :for="ids.role">Rolle</label>
      <app-select
        :id="ids.role"
        block
        :value="value.role"
        :items="roleChoices"
        placeholder="Rolle wählen"
        @input="set('role', $event)"
      />
    </div>
    <div v-if="mode === 'edit'" class="app-form-field">
      <label class="app-label" :for="ids.comment">Bemerkung</label>
      <app-text-field
        :id="ids.comment"
        textarea
        :rows="2"
        :value="value.comment"
        @input="set('comment', $event)"
      />
    </div>

    <!-- drei Zeilen ohne Rahmen mit Linie oben (Schalterzeilen als <label>, Passwort 48 hoch) -->
    <div class="user-ed__rows">
      <app-switch-row v-if="mode === 'edit'" :value="value.active" label="Aktiv" @input="set('active', $event)" />
      <app-switch-row :value="value.is_test_user" label="Testbenutzer" @input="set('is_test_user', $event)" />
      <div v-if="mode === 'edit' && user" class="user-ed__pw">
        <span class="user-ed__pw-label">Passwort</span>
        <span v-if="user.password_status === 'FORCE_CHANGE_PASSWORD'" class="user-ed__pw-status user-ed__pw-status--open">
          <app-icon name="clock" :size="16" />Erstanmeldung offen
        </span>
        <span v-else class="user-ed__pw-status">{{ passwordText }}</span>
        <app-button @click="$emit('change-password')">Ändern</app-button>
      </div>
    </div>
  </div>
</template>

<script>
// Inhalt des Detailbereichs „Benutzer bearbeiten“ bzw. „Benutzer anlegen“ (Spezifikation 4.4.5).
// Reine Darstellung: der Entwurf (value) gehört der Seite; jede Eingabe meldet field(key, wert), „Ändern“ meldet
// change-password. Bearbeiten: Kopf mit Avatar, Name, Rolle, Bemerkung, Schalter Aktiv/Testbenutzer, Passwortzeile.
// Anlegen (nicht entworfen, Festlegung): Benutzername und Benutzer-ID zusätzlich, ohne Bemerkung, Aktiv und Passwort.
export default {
  name: "UserEditor",
  props: {
    // Entwurf: { username, user_id, fullname, role, comment, active (Boolean), is_test_user (Boolean) }
    value: { type: Object, required: true },
    // gespeicherter Stand der Zeile (Kopf, Passwortstatus); im Anlegen-Modus null
    user: { type: Object, default: null },
    mode: { type: String, default: "edit" },
    // Rollen aus GET role ({ name, description })
    roles: { type: Array, default: () => [] },
    // Feldfehler { username, user_id, fullname } als Meldungstext
    errors: { type: Object, default: () => ({}) },
  },
  computed: {
    ids() {
      const base = "user-ed-" + this._uid;
      return {
        username: base + "-benutzername",
        userId: base + "-id",
        fullname: base + "-name",
        role: base + "-rolle",
        comment: base + "-bemerkung",
      };
    },
    displayName() {
      return this.user ? String(this.user.fullname || this.user.username || "") : "";
    },
    roleChoices() {
      const items = this.roles.map((r) => ({ value: r.name, text: r.description || r.name }));
      const current = this.value.role;
      // Rolle ohne Eintrag (Rollen noch nicht geladen): Code zeigen statt leerem Feld
      if (current && !items.some((i) => i.value === current)) {
        items.push({ value: current, text: String(current) });
      }
      return items;
    },
    passwordText() {
      const status = this.user ? this.user.password_status : null;
      if (status === "CONFIRMED") {
        return "Gesetzt";
      }
      return status ? String(status) : "–";
    },
  },
  methods: {
    set(key, value) {
      this.$emit("field", key, value);
    },
  },
};
</script>

<style scoped>
.user-ed {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.user-ed__head {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.user-ed__ident {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.user-ed__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--ink);
}
.user-ed__meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.user-ed__login {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-ed__lock {
  flex: none;
  width: 12px;
  height: 12px;
}
.user-ed__id {
  flex: none;
}
.user-ed__rows {
  display: flex;
  flex-direction: column;
}
.user-ed__pw {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 48px;
  box-sizing: border-box;
  padding: 4px 0;
  border-top: 1px solid var(--line-2);
}
.user-ed__pw-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ink);
}
.user-ed__pw-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
.user-ed__pw-status--open {
  font-weight: 500;
  color: var(--amber);
}
</style>
